import * as React from 'react';
import classNames from 'classnames';
import { Overlay as BlueprintOverlay } from '@blueprintjs/core';

import './styles.scss';
import Button, { IButtonProps } from '../Button';
import Buttons from '../Buttons';
import LayoutOverlay from '../../Layouts/LayoutOverlay';
import { IconsAvailable } from '../Icon';
import { IButtonIconProps } from '../ButtonIcon';

export interface IOverlayProps {
    /** Layouts to choose from */
    layout: 'dialog' | 'mainmenu' | 'focusview' | 'search' | 'category-tree';
    /** Transition between open and closed state */
    isOpen: boolean;
    /** Bring focus to opened overlay */
    autoFocus?: boolean;
    /** Triggered when closing transition starts */
    onClose?: (event?) => void;
    /** Triggered when transition ends */
    onClosed?: () => void;
    onOpened?: () => void;
    /** Closing function to initiate closing */
    doClose: (event?) => void;
    /** Overlay title for focusview */
    title?: string;
    /** zIndex stack order */
    zIndex?: number;
    /** className to append */
    className?: string;
    /** Dialog config */
    dialogConfig?: {
        /** Optional description to display */
        description?: React.ReactNode;
        /** Buttons to display */
        buttons?: IButtonProps[];
    };
    /** Render custom header above scrollable area */
    customHeader?: React.ReactNode;
    /** Container size in case of focusview layout is being used */
    size?: 'default' | 'max' | 'wide' | 'medium' | 'small';
    /** Icon to pass through to header */
    icon?: IconsAvailable;
    /** Buttons to display in the header */
    buttons?: IButtonProps[];
    /** ButtonIcon list to display in the header */
    buttonIcons?: IButtonIconProps[];
    /** Element to display in the header */
    headerAddOn?: React.ReactNode;
    /** Backdrop click closes overlay */
    canOutsideClickClose?: boolean;
    /** Escape key closes overlay */
    canEscapeKeyClose?: boolean;
    /** Transition name */
    transitionName?: 'transition-from-bottom' | 'transition-from-left' | 'transition-from-right';
}

interface IState {
}

/**
 * Overlay handler based on Blueprint overlay. Combine this with OverlayHOC and react-router to have URL/Hash based functionality.
 */
class Overlay extends React.Component<IOverlayProps, IState> {
    static displayName: string = 'Overlay';

    private nodeScroller;
    private cachedClientY = 0;
    private preventTouchBubbling = this.handlePreventTouchBubbling.bind(this);
    private cacheClientY = this.handleCacheClientY.bind(this);

    handleKeypress = (e) => {
        if (e.key === 'Escape') {
            if (!!this.props.canEscapeKeyClose && this.props.doClose) {
                this.props.doClose(e);
            } else if(this.props.canEscapeKeyClose === false) {
                e.stopPropagation();
            }
        }
    };

    handlePreventTouchBubbling (event) {
        if (this.nodeScroller && this.nodeScroller.clientHeight >= this.nodeScroller.scrollHeight) {
            return;
        }

        if (event.targetTouches.length !== 1) {
            return;
        }

        const clientY = event.targetTouches[0].clientY - this.cachedClientY;

        if (this.nodeScroller.scrollTop === 0 && clientY > 0) {
            event.preventDefault();
        }

        if ((this.nodeScroller.scrollHeight - this.nodeScroller.scrollTop <= this.nodeScroller.clientHeight) && clientY < 0) {
            event.preventDefault();
        }

    }

    handleCacheClientY (event) {
        if (this.nodeScroller && this.nodeScroller.clientHeight >= this.nodeScroller.scrollHeight) {
            return;
        }

        if (event.targetTouches.length !== 1) {
            return;
        }
        this.cachedClientY = event.targetTouches[0].clientY;
    }

    render () {
        let z = 20;
        if (this.props.zIndex) {
            z = this.props.zIndex + 1;
        } else if (this.props.layout === 'dialog') {
            z = 50;
        }

        return (
            <BlueprintOverlay
                isOpen={this.props.isOpen}
                onClose={this.props.onClose}
                onClosed={() => {
                    if (this.props.onClosed) {
                        this.props.onClosed();
                    }
                    window.dispatchEvent(new CustomEvent('ekaubamaja:overlay:closed'));
                }}
                onOpened={() => {
                    if (this.props.onOpened) {
                        this.props.onOpened();
                    }
                    // For updating all popper instances, which use getBoundingClientRect and get wrong positions due to css transitions.
                    window.dispatchEvent(new CustomEvent('resize'));
                    window.dispatchEvent(new CustomEvent('ekaubamaja:overlay:opened'));
                }}
                onOpening={() => {
                    window.dispatchEvent(new CustomEvent('resize'));
                    if (this.nodeScroller) {
                        this.nodeScroller.scrollTop = 0;
                    }
                }}
                transitionDuration={250}
                transitionName={this.props.transitionName}
                canOutsideClickClose={this.props.canOutsideClickClose}
                hasBackdrop={false}
                lazy={true}
                usePortal={true}
                canEscapeKeyClose={this.props.canEscapeKeyClose}
                enforceFocus={false}
                autoFocus={this.props.autoFocus !== undefined ? this.props.autoFocus : true}
            >
                <div className={classNames('overlayholder', 'overlayholder-' + this.props.layout)} style={{ zIndex: z, }} onKeyDown={this.handleKeypress}>
                    <div className={classNames('overlay-' + this.props.layout, this.props.className)}>
                        <div
                            className={'overlay-' + this.props.layout + '-holder'}
                            onClick={(e) => {
                                if (this.props.canOutsideClickClose && this.props.doClose) {
                                    this.props.doClose(e);
                                }
                            }}
                        >
                            <div
                                className={'overlay-' + this.props.layout + '-content'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                {this.props.customHeader}
                                <div
                                    className={'overlay-' + this.props.layout + '-scroller'}
                                    ref={(node) => {
                                        if (node) {
                                            this.nodeScroller = node;
                                            node.addEventListener('touchstart', this.cacheClientY, { passive: false });
                                            node.addEventListener('touchmove', this.preventTouchBubbling, { passive: false });
                                        }
                                    }}
                                >
                                    {this.props.layout === 'dialog' ? (
                                        <React.Fragment>
                                            {this.props.dialogConfig ? (
                                                <React.Fragment>
                                                    {!!this.props.title && <h2 className={'overlay-' + this.props.layout + '-title'}>{this.props.title}</h2>}
                                                    {!!this.props.dialogConfig.description && (
                                                        <div className="overlay-dialog-description">{this.props.dialogConfig.description}</div>
                                                    )}
                                                    {this.props.dialogConfig.buttons && !!this.props.dialogConfig.buttons.length && (
                                                        <Buttons layout="vertical-wide">
                                                            {this.props.dialogConfig.buttons.map((b, i) => (
                                                                <Button
                                                                    ref={(node) => {
                                                                        if (i === 0 && node) {
                                                                            // TODO
                                                                            // node.focus();
                                                                        }
                                                                    }}
                                                                    key={i}
                                                                    {...b}
                                                                    onClick={(e) => {
                                                                        this.props.doClose(e);
                                                                        if (b.onClick) {
                                                                            b.onClick(e);
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Buttons>
                                                    )}
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>{this.props.children}</React.Fragment>
                                            )}
                                        </React.Fragment>
                                    ) : (
                                        <LayoutOverlay
                                            title={this.props.title}
                                            size={this.props.size}
                                            icon={this.props.icon}
                                            buttons={this.props.buttons}
                                            buttonIcons={this.props.buttonIcons}
                                            addOn={this.props.headerAddOn}
                                        >
                                            {this.props.children}
                                        </LayoutOverlay>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {(this.props.layout === 'dialog' || this.props.layout === 'mainmenu') && (
                    <span
                        className="overlayholder-backdrop"
                        style={{ zIndex: z - 1, }}
                    />
                )}
            </BlueprintOverlay>
        );
    }
}

export default Overlay;
